/**
 * MCP Access Page
 *
 * The server has had 62 tools over an authenticated HTTP endpoint for a while
 * and no way for anyone to find out. It was mentioned in no README, surfaced on
 * no screen, and linked from no menu — you had to be told it existed.
 *
 * This page tells a clinic three things they cannot work out on their own:
 * where to point a client, that the credentials are the ones they already have,
 * and — the part with real consequences — which clinic their session will be
 * scoped to. The MCP endpoint pins a non-super-admin to their first clinic and
 * says nothing about it, so a multi-clinic user asking "how did we do today"
 * gets one brand's answer with no indication that is what happened.
 */

import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/authStore'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { MCP_TOOL_GROUPS } from '../config/mcpTools'
import { Copy, Check, Search, Info, Pencil } from 'lucide-react'
import { roleLabel } from '../lib/roles'

/**
 * Where this deployment's MCP endpoint lives.
 *
 * Derived from the page's own origin rather than configured: the API is served
 * from the same host as the app in every environment, and a hardcoded URL here
 * would send local users at production the first time someone forgot to change
 * it.
 */
function mcpEndpoint() {
  if (typeof window === 'undefined') return '/api/mcp'
  return `${window.location.origin}/api/mcp`
}

function CopyButton({ value, label = 'Copy' }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      // Clipboard is unavailable outside a secure context; the text is on
      // screen and selectable either way.
    }
  }

  return (
    <Button variant="outline" size="sm" onClick={copy}>
      {copied ? <Check className="mr-2 h-3.5 w-3.5 text-emerald-600" /> : <Copy className="mr-2 h-3.5 w-3.5" />}
      {copied ? 'Copied' : label}
    </Button>
  )
}

function CodeBlock({ children }) {
  return (
    <pre className="overflow-x-auto rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-foreground">
      <code>{children}</code>
    </pre>
  )
}

export default function McpAccessPage() {
  const { user, clinics } = useAuthStore()
  const [query, setQuery] = useState('')

  const endpoint = mcpEndpoint()
  const isSuperAdmin = user?.role === 'super_admin'

  // Mirrors authenticateBasic in backend/src/routes/mcp.js: super admins start
  // with no clinic selected, everyone else is pinned to their first.
  const pinnedClinicId = isSuperAdmin ? null : (user?.clinicIds?.[0] ?? null)
  const pinnedClinic = clinics.find(c => c.id === pinnedClinicId)
  const otherClinics = clinics.filter(c => c.id !== pinnedClinicId)

  const claudeCodeCommand = `claude mcp add --transport http core-management ${endpoint} \\
  --header "Authorization: Basic $(printf '%s' '${user?.email || 'you@example.com'}:YOUR_PASSWORD' | base64)"`

  const desktopConfig = JSON.stringify({
    mcpServers: {
      'core-management': {
        type: 'http',
        url: endpoint,
        headers: { Authorization: 'Basic <base64 of email:password>' },
      },
    },
  }, null, 2)

  const filteredGroups = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return MCP_TOOL_GROUPS
    return MCP_TOOL_GROUPS
      .map(group => ({
        ...group,
        tools: group.tools.filter(tool =>
          tool.name.includes(needle) || tool.description.toLowerCase().includes(needle)
        ),
      }))
      .filter(group => group.tools.length > 0)
  }, [query])

  const totalShown = filteredGroups.reduce((sum, group) => sum + group.tools.length, 0)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">MCP Access</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect an AI assistant to this data so you can ask it questions in plain language
          instead of reading a report.
        </p>
      </div>

      {/* The consequential part, so it goes first. */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">What your connection can see</CardTitle>
          <CardDescription>
            An assistant connects as you, and gets exactly the access your login already has —
            no more.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-muted-foreground">Signed in as</span>
            <span className="font-medium text-foreground">{user?.email}</span>
            <Badge variant="outline">{roleLabel(user?.role)}</Badge>
          </div>

          {isSuperAdmin ? (
            <p className="text-muted-foreground">
              Your session starts with <strong className="text-foreground">no clinic selected</strong>,
              so reports answer across every brand unless you name one. Ask for a specific brand and
              the assistant will pass it through.
            </p>
          ) : (
            <>
              <p className="text-muted-foreground">
                Your session is pinned to{' '}
                <strong className="text-foreground">{pinnedClinic?.displayName || pinnedClinicId || 'your clinic'}</strong>.
                Questions that do not name a brand are answered for that one.
              </p>
              {otherClinics.length > 0 && (
                <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                  You also have access to {otherClinics.map(c => c.displayName || c.id).join(', ')}, but an
                  assistant will not use {otherClinics.length === 1 ? 'it' : 'them'} unless you say so.
                  Name the brand in your question — <em>“revenue for {otherClinics[0].displayName || otherClinics[0].id} last week”</em> —
                  or the answer will quietly be about {pinnedClinic?.displayName || pinnedClinicId} instead.
                </p>
              )}
            </>
          )}

          <p className="flex items-start gap-2 text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Every date is Eastern time, matching every report in this app. “Today” means the
              Eastern day, not the assistant's local one.
            </span>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Connect</CardTitle>
          <CardDescription>
            Sign in with the same email and password you use here. There is no separate key to
            request, and nothing to install on this end.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-foreground">Endpoint</span>
              <CopyButton value={endpoint} />
            </div>
            <CodeBlock>{endpoint}</CodeBlock>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-foreground">Claude Code</span>
              <CopyButton value={claudeCodeCommand} />
            </div>
            <CodeBlock>{claudeCodeCommand}</CodeBlock>
            <p className="text-xs text-muted-foreground">
              Replace <code className="rounded bg-muted px-1">YOUR_PASSWORD</code> before running it.
              Your shell keeps a history — clear the entry afterwards, or type a leading space if
              your shell is set to skip those.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-foreground">Any other MCP client</span>
              <CopyButton value={desktopConfig} label="Copy JSON" />
            </div>
            <CodeBlock>{desktopConfig}</CodeBlock>
          </div>

          <p className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            Five wrong passwords locks the endpoint out for fifteen minutes, and requests are
            capped at sixty a minute. Both are per source address, so a shared office connection
            shares the allowance.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle className="text-base">What you can ask for</CardTitle>
              <CardDescription>
                {totalShown} {totalShown === 1 ? 'tool' : 'tools'}. You never name these — describe
                what you want and the assistant picks.
              </CardDescription>
            </div>
            <div className="relative w-56">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Search tools"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {filteredGroups.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nothing matches “{query.trim()}”.
            </p>
          )}

          {filteredGroups.map(group => (
            <div key={group.title}>
              <h3 className="text-sm font-semibold text-foreground">{group.title}</h3>
              <p className="text-xs text-muted-foreground">{group.blurb}</p>
              <ul className="mt-3 space-y-2.5">
                {group.tools.map(tool => (
                  <li key={tool.name} className="border-l-2 border-border pl-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="text-xs font-medium text-foreground">{tool.name}</code>
                      {tool.writes && (
                        <Badge variant="outline" className="gap-1 border-amber-300 text-amber-800">
                          <Pencil className="h-3 w-3" />
                          changes data
                        </Badge>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                      {tool.description}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
